import { BackendError } from '../../../domain/error.domain';
import { Account, CashBalanceType } from '../../../domain/account.domain';
import { ResponseStatus } from '../securities/securities-transfers.domain';

export interface CashTransfer {
  selectedFromAccount: CashTransferAccount;
  toAccounts: Array<CashTransferAccount>;
  selectedToAccount: CashTransferAccount;
  contributionType?: string;
  transferAmount: number;
  currency: string;
  isRecurringTransfer?: boolean;
  frequencyType?: FrequencyTypes;
  fromAccountSessionKey?: string;
  planType?: PlanTypes;
  startDate?: Date;
  success: boolean;
}

export interface CashTransferValidation {
  conversionRate: number;
  convertedAmount: number;
  convertedCurrency: string;
  displayTransferCutoverMessage: boolean;
  rateExpiryInSeconds: number;
  transferControlNumber: string;
}

export interface CashTransferConfirmation {
  confirmationNumber: string;
  effectiveDate?: Date;
  errors?: Array<CashTransferError>;
  wmmCashTransfer?: boolean;
}

export interface CashTransferStatusResponse {
  transferStatusCd: string;
  dasReferenceId?: string;
  status?: ResponseStatus; // This comes from Security Transfer domain but this is the same object that the backend is using as well
}

export interface CashTransferError extends BackendError {
  message?: string;
}

export interface AT2FundProfile {
  agreementNum: string; // Account number
  cashAmt: number;
  cashWithoutLoanAmt: number;
  netLiquidationAmt: number;
  unclearedBuyingPowerAmt: number;
  clearedBuyingPowerAmt: number;
}

export interface FundProfile {
  cashAmt: number;
  clearedBuyingPowerAmt: number;
}

export class CashTransferErrorHelper {
  static isMessageEmpty(error: CashTransferError): boolean {
    return !(error.message?.length > 0);
  }
}

// TODO convert those enums into types, new TS no likey enums

export enum ActiveStep {
  CREATE = 'create',
  REVIEW = 'review',
  CONFIRM = 'confirm',
  FAIL = 'fail'
}

export enum ContributionTypes {
  PERSONAL = 'PERSONAL',
  SPOUSAL = 'SPOUSAL'
}

export enum FrequencyTypes {
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  SEMI_MONTHLY = 'SEMI_MONTHLY',
  MONTHLY = 'MONTHLY',
  ONE_TIME = 'ONE_TIME'
}

export enum TransferToggleValues {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING'
}

export enum PlanTypes {
  MCP = 'MCP',
  PAD = 'PAD'
}

export enum Day {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY'
}

export interface CashTransferAccount extends Account {
  cashBalance: CashBalanceType;
  sessionKey: string;
  rrCode?: string; // Used by cash transfers to identify ActiveTrader 2.0 accounts, rrCode would be 'RMPX'
  planCode?: string;
  ineligibleAccount?: boolean;
  tdctTfsaHisaAccount?: boolean; // New flag used to indicate particular account for special transfer type
  tdctRrspDisaAccount?: boolean; // New flag used to indicate if the account is a TDCT DISA RRSP account
  transferFromTDCTAccount?: boolean;
  transferToDIAccount?: boolean;
  spousalAccount?: boolean; // Used to indicate if the account is a spousal account
}

export interface CashTransferValues {
  currency: string;
  amount: number;
}

export interface CashTransferBalanceByCurrency {
  CAD: CashTransferValues;
  USD: CashTransferValues;
}

export interface CashTransferGroup {
  groupNumber: string;
  totalMarginBalance?: CashTransferBalanceByCurrency;
}

export type CashTransferAggregationTable = Record<string, CashTransferGroup>;

export interface CashTransferAggregationResponseContent {
  aggregationResponseType: string;
  result: CashTransferAggregationTable;
  status: string;
}

export interface CashTransferAggregationResponseWrapper {
  ACCOUNT_MARGIN_DATA_TOTAL_GROUPED_BY_NUMBER: CashTransferAggregationResponseContent;
}

export type TicketStatus = 'IN_PROGRESS' | 'REJECTED' | 'COMPLETED' | 'UNKNOWN_STATUS';
export type TicketType = 'MCP_PAD' | 'TFSA' | 'DISA';

export interface RecurringCashTransferStatus {
  ticketNumber: string;
  ticketStatus: TicketStatus;
  ticketType?: TicketType;
  problemId: string;
  fromAccount?: CashTransferAccount;
  toAccount?: CashTransferAccount;
  createdDate: Date;
  updatedDate: Date;
  parsedFreeFormDetail?: FreeFormDetail;
}

export interface FreeFormDetail {
  fromAccount: string;
  toAccount: string;
  playType?: string;
  frequencyType?: string;
  transferAmount: string;
  startDate?: string;
  institutionNumber?: string;
  transitNumber?: string;
  contributionType?: string;
}
